import { Link } from "react-router-dom";
import { ArrowRight, Star, PhoneCall, MapPin } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { CategoryIcon } from "./CategoryIcon";
import { telHref } from "../lib/api";
import { Badge } from "./ui/badge";

export const OrgCard = ({ org, index = 0 }) => {
  const { t, catLabel } = useLang();
  const c = org.contact || {};
  const phone = c.freephone || c.phone;
  const phoneNumber = telHref(phone);

  return (
    <div
      data-testid={`org-card-${org._id}`}
      className="card-lift fade-up flex flex-col bg-white rounded-2xl border border-stone-200 p-6 shadow-sm h-full"
      style={{ animationDelay: `${Math.min(index * 60, 400)}ms` }}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex flex-wrap gap-1.5">
          {(org.categories || []).slice(0, 2).map((cat) => (
            <Badge
              key={cat}
              className="rounded-full font-semibold text-[11px] border border-stone-300 bg-stone-100 text-stone-900"
            >
              <CategoryIcon category={cat} className="w-3 h-3 mr-1" />
              {catLabel(cat)}
            </Badge>
          ))}
        </div>
        {org.featured && (
          <span className="flex items-center gap-1 text-[11px] font-bold text-[#B8892F] shrink-0">
            <Star className="w-3.5 h-3.5" fill="currentColor" strokeWidth={0} />
            {t("featured")}
          </span>
        )}
      </div>

      <Link to={`/organisation/${org._id}`} data-testid={`org-link-${org._id}`} className="group block">
        <h3 className="font-display font-bold text-lg text-stone-900 leading-snug mb-2 group-hover:text-[#2E604A] transition-colors">{org.name}</h3>
        <p className="text-sm text-stone-600 leading-relaxed line-clamp-3">{org.short_description}</p>
      </Link>

      <div className="mt-4 pt-4 border-t border-stone-100 flex flex-wrap items-center gap-2">
        {phoneNumber && (
          <a
            href={`tel:${phoneNumber}`}
            data-testid={`call-${org._id}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#2E604A] text-white text-xs font-semibold px-3.5 py-2 hover:bg-[#234B3A] transition-colors"
          >
            <PhoneCall className="w-3.5 h-3.5" /> {t("call_now")}
          </a>
        )}
        {c.address && (
          <a
            href={`https://maps.google.com/?q=${encodeURIComponent(c.address + ", Auckland, New Zealand")}`}
            target="_blank"
            rel="noreferrer"
            data-testid={`maps-${org._id}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 text-stone-700 text-xs font-semibold px-3.5 py-2 hover:border-[#2E604A] hover:text-[#2E604A] transition-colors"
          >
            <MapPin className="w-3.5 h-3.5" /> {t("open_maps")}
          </a>
        )}
        <Link
          to={`/organisation/${org._id}`}
          className="ms-auto inline-flex items-center gap-1 text-sm font-semibold text-[#2E604A] hover:gap-2 transition-all"
        >
          {t("contact")} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
        </Link>
      </div>
    </div>
  );
};
